import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Toast } from 'antd-mobile';
import { ClockCircleOutline } from 'antd-mobile-icons';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { selectCurrentScooter } from '../store/slices/scooterSlice';
import {
  selectWalletBalance,
  showTopupModal,
  fetchWalletBalance,
} from '../store/slices/walletSlice';
import { packService } from '../services/api';
import type { PackTemplate } from '../services/api/types';
import EnergyPackPurchaseModal from '../components/organisms/EnergyPackPurchaseModal';
import { EnergyBatteryIcon } from '../components/icons';
import { r } from '../theme/riderTheme';
import {
  PageHeader,
  PageHeaderEyebrow,
  PageHeaderText,
  PageHeaderTitle,
  PrimaryButton,
  GhostButton,
  ButtonChevron,
} from '../theme/surfacePrimitives';
import { formatValidityHours } from '../utils/packValidity';

type PackTypeFilter = 'all' | 'energy_only' | 'time_based' | 'hybrid';
type SortBy = 'price_asc' | 'price_desc' | 'energy' | 'duration';
type BadgeVariant = 'featured' | 'recommended' | 'popular';
type SpecKind = 'energy' | 'time';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SearchField = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 14px 0 38px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 12px;
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  color: ${({ theme }) => r(theme).text.primary};
  transition: border-color 160ms ease, box-shadow 160ms ease;

  &::placeholder {
    color: ${({ theme }) => r(theme).text.faint};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => r(theme).text.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }
`;

const SearchGlyph = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid ${({ theme }) => r(theme).text.faint};
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    left: 12px;
    top: 12px;
    width: 6px;
    height: 1.5px;
    background: ${({ theme }) => r(theme).text.faint};
    transform: rotate(45deg);
    transform-origin: 0 0;
  }
`;

const ClearSearch = styled.button.attrs({ type: 'button' as const, 'aria-label': 'Clear search' })`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: transparent;
  border: 0;
  color: ${({ theme }) => r(theme).text.faint};
  cursor: pointer;
  font-size: 18px;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => r(theme).text.primary};
  }
`;

const FilterStripe = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  padding: 2px 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TypeChip = styled.button.attrs({ type: 'button' as const })<{ $active: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border-radius: 999px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 160ms ease, color 160ms ease, border-color 160ms ease;

  ${({ $active, theme }) => {
    const t = r(theme);
    return $active
      ? css`
          background: ${t.cta.bg};
          border: 1px solid ${t.cta.bg};
          color: ${t.cta.text};
        `
      : css`
          background: ${t.surface.sheet};
          border: 1px solid ${t.surface.edge};
          color: ${t.text.muted};

          &:hover {
            color: ${t.text.primary};
            border-color: ${t.surface.edgeStrong};
          }
        `;
  }}

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }
`;

const ChipCount = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  opacity: 0.7;
`;

const SortDock = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const SortTrigger = styled.button.attrs({ type: 'button' as const })<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border-radius: 999px;
  background: ${({ theme, $open }) =>
    $open ? r(theme).surface.raisedSelected : r(theme).surface.sheet};
  border: 1px solid
    ${({ theme, $open }) =>
      $open ? r(theme).accent.limeDeep : r(theme).surface.edge};
  color: ${({ theme }) => r(theme).text.primary};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }
`;

const SortGlyph = styled.span<{ $open: boolean }>`
  display: inline-block;
  font-size: 10px;
  line-height: 1;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 200ms ease;
`;

const SortMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  min-width: 210px;
  padding: 6px;
  background: ${({ theme }) => r(theme).surface.sheet};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 12px;
  box-shadow: 0 12px 40px -12px rgba(18, 20, 25, 0.18);
`;

const SortOption = styled.button.attrs({ type: 'button' as const })<{ $active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 10px;
  background: ${({ theme, $active }) =>
    $active ? r(theme).surface.raisedSelected : 'transparent'};
  border: 0;
  border-radius: 8px;
  color: ${({ theme }) => r(theme).text.primary};
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: background 140ms ease;

  &:hover {
    background: ${({ theme }) => r(theme).surface.inputIdle};
  }
`;

const SortBadge = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).accent.ok};
`;

const WalletStrip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => r(theme).cta.bg};
  border-radius: 12px;
  color: ${({ theme }) => r(theme).text.inverse};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const WalletStripValue = styled.span`
  font-family: 'Geist', sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-transform: none;
  color: ${({ theme }) => r(theme).accent.lime};
`;

const PackGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Card = styled.article<{ $featured?: boolean }>`
  position: relative;
  padding: 18px 18px 16px;
  background: ${({ theme }) => r(theme).surface.sheet};
  border: 1px solid
    ${({ theme, $featured }) =>
      $featured ? r(theme).text.primary : r(theme).surface.edge};
  border-radius: 16px;
  transition: transform 200ms cubic-bezier(0.2, 0, 0, 1), box-shadow 220ms ease;
  display: flex;
  flex-direction: column;
  gap: 14px;

  ${({ $featured, theme }) =>
    $featured &&
    css`
      box-shadow: 0 0 0 3px ${r(theme).glow.selectionLime};

      &::after {
        content: '';
        position: absolute;
        top: 14px;
        right: 14px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${r(theme).accent.lime};
        box-shadow: 0 0 10px ${r(theme).accent.lime};
      }
    `}

  &:hover {
    transform: translateY(-1px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const PackName = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: ${({ theme }) => r(theme).text.primary};
  margin-bottom: 6px;
  overflow-wrap: anywhere;
`;

const PackKindBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  color: ${({ theme }) => r(theme).text.muted};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
`;

const PriceColumn = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const PriceCurrency = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

const PriceValue = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1;
  color: ${({ theme }) => r(theme).text.primary};
`;

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const badgeColors = (theme: unknown, variant: BadgeVariant) => {
  const t = r(theme);
  switch (variant) {
    case 'featured':
      return { bg: t.surface.raisedSelected, border: t.accent.limeDeep, text: t.accent.ok };
    case 'recommended':
      return { bg: 'rgba(0, 215, 255, 0.06)', border: 'rgba(0, 215, 255, 0.32)', text: '#0884a0' };
    case 'popular':
    default:
      return { bg: 'rgba(245, 158, 11, 0.06)', border: 'rgba(245, 158, 11, 0.32)', text: '#b45309' };
  }
};

const Badge = styled.span<{ $variant: BadgeVariant }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ theme, $variant }) => badgeColors(theme, $variant).bg};
  border: 1px solid ${({ theme, $variant }) => badgeColors(theme, $variant).border};
  color: ${({ theme, $variant }) => badgeColors(theme, $variant).text};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
`;

const Specs = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
`;

const Spec = styled.div`
  padding: 12px 14px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const SpecMark = styled.span<{ $kind: SpecKind }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 22px;
  border: 1px solid
    ${({ theme, $kind }) =>
      $kind === 'energy' ? r(theme).accent.limeDeep : r(theme).surface.edgeStrong};
  background: ${({ theme, $kind }) =>
    $kind === 'energy' ? r(theme).surface.raisedSelected : r(theme).surface.sheet};
  color: ${({ theme, $kind }) =>
    $kind === 'energy' ? r(theme).accent.ok : r(theme).text.muted};
  border-radius: 999px;

  & > svg {
    width: 14px;
    height: 14px;
    display: block;
  }
`;

const SpecValue = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: ${({ theme }) => r(theme).text.primary};
`;

const SpecLabel = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

const Description = styled.p`
  margin: 0;
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => r(theme).text.muted};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TopUpNeeded = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #b45309;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #f59e0b;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px dashed ${({ theme }) => r(theme).surface.edge};
  border-radius: 14px;
`;

const EmptyTitle = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => r(theme).text.primary};
  margin-bottom: 6px;
`;

const EmptyBody = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  color: ${({ theme }) => r(theme).text.muted};
  margin-bottom: 16px;
`;

const ClearAllButton = styled.button.attrs({ type: 'button' as const })`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid ${({ theme }) => r(theme).surface.edgeStrong};
  border-radius: 999px;
  color: ${({ theme }) => r(theme).text.primary};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => r(theme).surface.inputIdle};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }
`;

const LoadingShell = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.muted};
  text-align: center;
  padding: 56px 20px;
`;

const packTypeCopy = (type: string): string => {
  switch (type) {
    case 'energy_only':
      return 'Energy only';
    case 'time_based':
      return 'Time based';
    case 'hybrid':
      return 'Hybrid';
    default:
      return type;
  }
};

const typeFilters: Array<{ value: PackTypeFilter; label: string }> = [
  { value: 'all', label: 'All packs' },
  { value: 'energy_only', label: 'Energy' },
  { value: 'time_based', label: 'Time' },
  { value: 'hybrid', label: 'Hybrid' },
];

const sortOptions: Array<{ value: SortBy; label: string }> = [
  { value: 'price_asc', label: 'Price · Low to High' },
  { value: 'price_desc', label: 'Price · High to Low' },
  { value: 'energy', label: 'Energy · High to Low' },
  { value: 'duration', label: 'Duration · Long to Short' },
];

const PackTemplateBrowserPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const selectedScooter = useAppSelector(selectCurrentScooter);
  const walletBalance = useAppSelector(selectWalletBalance);

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<PackTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [packType, setPackType] = useState<PackTypeFilter>('all');
  const [sortBy, setSortBy] = useState<SortBy>('price_asc');
  const [sortOpen, setSortOpen] = useState(false);
  const [purchaseTemplateId, setPurchaseTemplateId] = useState<string | null>(null);

  const sortDockRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScooter?.vin]);

  useEffect(() => {
    dispatch(fetchWalletBalance());
  }, [dispatch]);

  useEffect(() => {
    if (!sortOpen) return;
    const handler = (event: MouseEvent) => {
      if (!sortDockRef.current?.contains(event.target as Node)) {
        setSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [sortOpen]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await packService.getPackTemplates({
        scooter_vin: selectedScooter?.vin,
        is_active: true,
      });
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load pack templates:', err);
      Toast.show({ content: 'Failed to load pack templates', position: 'bottom' });
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let list = templates;

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      list = list.filter(t =>
        t.name.toLowerCase().includes(query) ||
        (t.description ?? '').toLowerCase().includes(query),
      );
    }

    if (packType !== 'all') {
      list = list.filter(t => t.pack_type === packType);
    }

    const sorted = [...list];
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'price_asc':
          return a.price_npr - b.price_npr;
        case 'price_desc':
          return b.price_npr - a.price_npr;
        case 'energy':
          return (b.ah_limit || 0) - (a.ah_limit || 0);
        case 'duration':
          return (b.validity_hours || 0) - (a.validity_hours || 0);
        default:
          return 0;
      }
    });

    return sorted;
  }, [templates, searchQuery, packType, sortBy]);

  const typeCounts = useMemo(() => {
    const base: Record<PackTypeFilter, number> = {
      all: templates.length,
      energy_only: 0,
      time_based: 0,
      hybrid: 0,
    };
    for (const t of templates) {
      if (t.pack_type === 'energy_only') base.energy_only += 1;
      else if (t.pack_type === 'time_based') base.time_based += 1;
      else if (t.pack_type === 'hybrid') base.hybrid += 1;
    }
    return base;
  }, [templates]);

  const currentBalance = walletBalance?.current_balance ?? 0;
  const hasFilters = searchQuery.trim().length > 0 || packType !== 'all';

  const canAfford = (pack: PackTemplate): boolean => currentBalance >= pack.price_npr;
  const topUpAmount = (pack: PackTemplate): number => Math.max(0, pack.price_npr - currentBalance);

  const handleBuy = (pack: PackTemplate) => {
    if (!selectedScooter) {
      Toast.show({ content: 'Please select a scooter first', position: 'bottom' });
      return;
    }
    if (!canAfford(pack)) {
      dispatch(showTopupModal());
      return;
    }
    setPurchaseTemplateId(pack.id);
  };

  const handlePurchaseSuccess = () => {
    void dispatch(fetchWalletBalance());
    void loadTemplates();
    setPurchaseTemplateId(null);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setPackType('all');
  };

  const packBadges = (pack: PackTemplate): Array<{ variant: BadgeVariant; text: string }> => {
    const badges: Array<{ variant: BadgeVariant; text: string }> = [];
    if (pack.is_featured) badges.push({ variant: 'featured', text: 'Featured' });
    if (pack.recommended_for_scooter === selectedScooter?.vin) {
      badges.push({ variant: 'recommended', text: 'Recommended' });
    }
    if (pack.price_npr <= 2000) badges.push({ variant: 'popular', text: 'Popular' });
    return badges;
  };

  const packSpecs = (pack: PackTemplate): Array<{ kind: SpecKind; value: string; label: string }> => {
    const specs: Array<{ kind: SpecKind; value: string; label: string }> = [];
    if (pack.ah_limit) {
      specs.push({ kind: 'energy', value: `${pack.ah_limit} Ah`, label: 'Energy' });
    }
    const validityLabel = formatValidityHours(pack.validity_hours);
    if (validityLabel) {
      specs.push({
        kind: 'time',
        value: validityLabel,
        label: 'Validity',
      });
    }
    return specs;
  };

  const formatNpr = (amount: number) =>
    `NPR ${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  return (
    <Container>
      <PageHeader>
        <PageHeaderText>
          <PageHeaderEyebrow>Pack · Browse</PageHeaderEyebrow>
          <PageHeaderTitle>All packs</PageHeaderTitle>
        </PageHeaderText>
      </PageHeader>

      <WalletStrip>
        <span>Wallet balance</span>
        <WalletStripValue>{formatNpr(currentBalance)}</WalletStripValue>
      </WalletStrip>

      <SearchField>
        <SearchGlyph aria-hidden />
        <SearchInput
          type="text"
          placeholder="Search pack templates"
          value={searchQuery}
          onChange={event => setSearchQuery(event.target.value)}
        />
        {searchQuery && (
          <ClearSearch onClick={() => setSearchQuery('')}>×</ClearSearch>
        )}
      </SearchField>

      <FilterStripe>
        {typeFilters.map(filter => (
          <TypeChip
            key={filter.value}
            $active={packType === filter.value}
            onClick={() => setPackType(filter.value)}
          >
            <span>{filter.label}</span>
            <ChipCount>{typeCounts[filter.value]}</ChipCount>
          </TypeChip>
        ))}

        <SortDock ref={sortDockRef}>
          <SortTrigger
            $open={sortOpen}
            onClick={() => setSortOpen(open => !open)}
            aria-haspopup="menu"
            aria-expanded={sortOpen}
          >
            <span>Sort</span>
            <SortGlyph $open={sortOpen} aria-hidden>▾</SortGlyph>
          </SortTrigger>
          {sortOpen && (
            <SortMenu role="menu">
              {sortOptions.map(option => (
                <SortOption
                  key={option.value}
                  $active={sortBy === option.value}
                  onClick={() => {
                    setSortBy(option.value);
                    setSortOpen(false);
                  }}
                  role="menuitemradio"
                  aria-checked={sortBy === option.value}
                >
                  <span>{option.label}</span>
                  {sortBy === option.value && <SortBadge>Active</SortBadge>}
                </SortOption>
              ))}
            </SortMenu>
          )}
        </SortDock>
      </FilterStripe>

      {loading ? (
        <LoadingShell>Loading pack templates…</LoadingShell>
      ) : filtered.length === 0 ? (
        <EmptyState>
          <EmptyTitle>{hasFilters ? 'No packs match your filters' : 'No packs available'}</EmptyTitle>
          <EmptyBody>
            {hasFilters
              ? 'Try a different type or clear the search.'
              : 'Check back later for new pack options.'}
          </EmptyBody>
          {hasFilters && <ClearAllButton onClick={clearFilters}>Clear filters</ClearAllButton>}
        </EmptyState>
      ) : (
        <PackGrid>
          {filtered.map(pack => {
            const specs = packSpecs(pack);
            const badges = packBadges(pack);
            const affordable = canAfford(pack);
            const needed = topUpAmount(pack);
            return (
              <Card key={pack.id} $featured={pack.is_featured}>
                <CardHeader>
                  <TitleBlock>
                    <PackName>{pack.name}</PackName>
                    <PackKindBadge>{packTypeCopy(pack.pack_type)}</PackKindBadge>
                  </TitleBlock>
                  <PriceColumn>
                    <PriceCurrency>NPR</PriceCurrency>
                    <PriceValue>{(pack.price_npr ?? 0).toLocaleString('en-IN')}</PriceValue>
                  </PriceColumn>
                </CardHeader>

                {badges.length > 0 && (
                  <Badges>
                    {badges.map(badge => (
                      <Badge key={badge.text} $variant={badge.variant}>
                        {badge.text}
                      </Badge>
                    ))}
                  </Badges>
                )}

                {specs.length > 0 && (
                  <Specs>
                    {specs.map(spec => (
                      <Spec key={spec.label}>
                        <SpecMark $kind={spec.kind} aria-hidden>
                          {spec.kind === 'energy' ? (
                            <EnergyBatteryIcon />
                          ) : (
                            <ClockCircleOutline />
                          )}
                        </SpecMark>
                        <SpecValue>{spec.value}</SpecValue>
                        <SpecLabel>{spec.label}</SpecLabel>
                      </Spec>
                    ))}
                  </Specs>
                )}

                {pack.description && <Description>{pack.description}</Description>}

                <Actions>
                  {affordable ? (
                    <PrimaryButton onClick={() => handleBuy(pack)}>
                      <span>Buy now</span>
                      <ButtonChevron>→</ButtonChevron>
                    </PrimaryButton>
                  ) : (
                    <GhostButton onClick={() => handleBuy(pack)}>
                      <span>Top up &amp; buy</span>
                    </GhostButton>
                  )}
                  {!affordable && <TopUpNeeded>Need {formatNpr(needed)} more</TopUpNeeded>}
                </Actions>
              </Card>
            );
          })}
        </PackGrid>
      )}

      {selectedScooter && (
        <EnergyPackPurchaseModal
          visible={purchaseTemplateId !== null}
          scooterVin={selectedScooter.vin}
          walletBalance={currentBalance}
          initialTemplateId={purchaseTemplateId}
          onClose={() => setPurchaseTemplateId(null)}
          onSuccess={handlePurchaseSuccess}
        />
      )}
    </Container>
  );
};

export default PackTemplateBrowserPage;
